import * as React from "react";
import { FormattedMessage, WrappedComponentProps, injectIntl } from "react-intl";
import { connect } from "react-redux";
import PageHeader from "components/PageHeader";
import Typography from "components/Typography";
import Consent from "components/Consent";
import { ContainerLoader } from "components/Loading";
import { ApplicationState } from "stores";
import { OrganizationWithConsents, Consent as ConsentType, MarketingConsent } from "types";
import { fetchConsents, changeConsent } from "stores/consents";
import {
  fetchHooplaMarketingConsents,
  fetchOrganizationMarketingConsents,
  updateHooplaMarketingConsent,
  updateOrganizationMarketingConsent,
} from "stores/marketingConsents";
import * as styles from "./styles";

interface IProps {
  userId: number;
  fetchConsents: (userId: number) => void;
  fetchHooplaMarketingConsents: (userId: number) => void;
  fetchOrganizationMarketingConsents: (userId: number) => void;
  loading: boolean;
  organizations: OrganizationWithConsents[];
  hooplaConsent: MarketingConsent | null;
  orgConsents: MarketingConsent[];
  patching: Set<number>;
  patchingOrg: Set<number>;
  patchingHoopla: boolean;
  error: boolean;
  changeConsent: (
    value: boolean,
    userId: number,
    consentRequestId: number,
    purchaseId: number,
  ) => void;
  updateHooplaMarketingConsent: (value: boolean, userId: number) => void;
  updateOrganizationMarketingConsent: (
    value: boolean,
    userId: number,
    organizationId: number,
  ) => void;
}

class Privacy extends React.Component<IProps & WrappedComponentProps> {
  componentDidMount() {
    this.props.fetchConsents(this.props.userId);
    this.props.fetchHooplaMarketingConsents(this.props.userId);
    this.props.fetchOrganizationMarketingConsents(this.props.userId);
  }

  toggleConsent = (val: boolean, consent: ConsentType) => {
    const purchaseId = consent.consent ? consent.consent.purchaseId : null;
    this.props.changeConsent(
      val,
      this.props.userId,
      consent.consentRequest.consentRequestId,
      purchaseId,
    );
  };

  toggleHooplaMarketingConsent = (val: boolean) => {
    this.props.updateHooplaMarketingConsent(val, this.props.userId);
  };

  toggleOrganizationMarketingConsent = (val: boolean, org: number) => {
    this.props.updateOrganizationMarketingConsent(val, this.props.userId, org);
  };

  render() {
    if (this.props.loading) {
      return <ContainerLoader />;
    }

    if (this.props.error) {
      return (
        <React.Fragment>
          <PageHeader>
            <FormattedMessage
              id="containers.Privacy.otherConsents"
              defaultMessage="Other consents"
            />
          </PageHeader>
          <Typography type="subheading">
            <FormattedMessage id="containers.Privacy.genericError" />
          </Typography>
        </React.Fragment>
      );
    }

    return (
      <React.Fragment>
        {(this.props.hooplaConsent !== null || this.props.orgConsents.length > 0) && (
          <div className={styles.organization}>
            <PageHeader>
              <FormattedMessage
                id="containers.Privacy.marketingConsents"
                defaultMessage="Marketing consents"
              />
            </PageHeader>
            {this.props.hooplaConsent !== null && (
              <Consent
                text={this.props.intl.formatMessage({
                  id: "containers.Privacy.hooplaMarketing",
                  defaultMessage: "I want to receive news from Hoopla",
                })}
                consented={this.props.hooplaConsent.is_active}
                patching={this.props.patchingHoopla}
                expiry={
                  this.props.hooplaConsent.is_active ? this.props.hooplaConsent.expiry_date : null
                }
                onChange={(v) => this.toggleHooplaMarketingConsent(v)}
              />
            )}
            {this.props.orgConsents.length > 0 && (
              <div>
                {this.props.orgConsents.map((oc) => (
                  <Consent
                    key={oc.organization_id}
                    text={this.props.intl.formatMessage(
                      {
                        id: "containers.Privacy.orgMarketing",
                        defaultMessage: "I want to receive relevant info and offers from {orgname}",
                      },
                      { orgname: oc.organization_name },
                    )}
                    consented={oc.is_active}
                    patching={this.props.patchingOrg.has(oc.organization_id)}
                    expiry={oc.is_active ? oc.expiry_date : null}
                    onChange={(v) => this.toggleOrganizationMarketingConsent(v, oc.organization_id)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
        <PageHeader>
          <FormattedMessage id="containers.Privacy.otherConsents" defaultMessage="Other consents" />
        </PageHeader>
        {this.props.organizations.map((organization) => (
          <div key={organization.organizationId} className={styles.organization}>
            <div className={styles.organizationName}>
              <Typography type="subheading">{organization.name}</Typography>
            </div>
            {organization.consents.map((consent) => (
              <Consent
                key={consent.consentRequest.consentRequestId}
                text={consent.consentRequest.text}
                consented={!!consent.consent}
                patching={this.props.patching.has(consent.consentRequest.consentRequestId)}
                expiry={consent.consent ? consent.consent.expires : null}
                onChange={(v) => this.toggleConsent(v, consent)}
              />
            ))}
          </div>
        ))}
        {this.props.organizations.length === 0 && (
          <Typography type="subheading">
            <FormattedMessage id="containers.Privacy.consentsEmpty" />
          </Typography>
        )}
      </React.Fragment>
    );
  }
}

export default connect(
  (state: ApplicationState) => ({
    loading:
      (state.consents.fetching || !state.consents.fetched) &&
      (state.marketingConsents.fetchingHoopla || !state.marketingConsents.fetchedHoopla) &&
      (state.marketingConsents.fetchingOrg || !state.marketingConsents.fetchedOrg),
    error: state.consents.error || state.marketingConsents.error,
    organizations: state.consents.organizations,
    patching: state.consents.patching,
    patchingOrg: state.marketingConsents.patchingOrg,
    patchingHoopla: state.marketingConsents.patchingHoopla,
    orgConsents: state.marketingConsents.orgConsents,
    hooplaConsent: state.marketingConsents.hooplaConsent,
    userId: state.user.userData.userId,
  }),
  {
    fetchConsents,
    changeConsent,
    fetchHooplaMarketingConsents,
    fetchOrganizationMarketingConsents,
    updateHooplaMarketingConsent,
    updateOrganizationMarketingConsent,
  },
)(injectIntl(Privacy));
